import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useProviderStore } from "core/store/providerStore";
import { invalidateFleetQueries } from "core/query/invalidateFleet";
import {
  getVehicle,
  submitVerification,
} from "features/vehicles/services/vehicleApi";
import { uploadVehicleVerificationDocumentAndWait } from "features/vehicles/services/vehicleApi";
import { ROUTES } from "core/router/routes";
import type { DocumentType, VehicleResponse } from "types/api.generated";

type DocForm = {
  file: File | null;
  expiresAt: string;
  url: string | null;
  uploading: boolean;
};

const DOC_LABELS: Record<DocumentType, { label: string; required: boolean }> = {
  registration: { label: "Registration", required: true },
  insurance: { label: "Insurance", required: false },
  roadworthiness: { label: "Roadworthiness", required: false },
};

const emptyDoc = (): DocForm => ({
  file: null,
  expiresAt: "",
  url: null,
  uploading: false,
});

export default function VehicleVerify() {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeOrgId, vehicles, setActiveVehicleId, setActiveOrgId, refreshVehicles } =
    useProviderStore();

  const [vehicle, setVehicle] = useState<VehicleResponse | null>(null);
  const [plateNumber, setPlateNumber] = useState("");
  const [docs, setDocs] = useState<Record<DocumentType, DocForm>>({
    registration: emptyDoc(),
    insurance: emptyDoc(),
    roadworthiness: emptyDoc(),
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vehicleId) return;
    setActiveVehicleId(vehicleId);
    getVehicle(vehicleId)
      .then((v) => {
        setVehicle(v);
        setPlateNumber(v.plateNumber ?? "");
        if (v.organizationId) setActiveOrgId(v.organizationId);
      })
      .catch(() => setError("Could not load vehicle."))
      .finally(() => setLoading(false));
  }, [vehicleId, navigate, setActiveVehicleId, setActiveOrgId]);

  const orgId = activeOrgId ?? vehicle?.organizationId ?? "";

  const uploadDoc = async (
    type: DocumentType,
    file?: File,
  ): Promise<string | null> => {
    const docFile = file ?? docs[type].file;
    if (!docFile || !vehicleId) return null;
    if (!orgId) {
      setError(
        "Organisation context is missing. Go back to setup and try again.",
      );
      return null;
    }

    setDocs((prev) => ({
      ...prev,
      [type]: { ...prev[type], uploading: true },
    }));
    try {
      const url = await uploadVehicleVerificationDocumentAndWait(
        vehicleId,
        docFile,
        type,
        orgId,
      );
      setDocs((prev) => ({
        ...prev,
        [type]: { ...prev[type], file: docFile, url, uploading: false },
      }));
      return url;
    } catch (err: any) {
      setError(err?.message || "Upload failed");
      setDocs((prev) => ({
        ...prev,
        [type]: { ...prev[type], uploading: false },
      }));
      return null;
    }
  };

  const handleFile = (type: DocumentType, file: File | null) => {
    setDocs((prev) => ({
      ...prev,
      [type]: { ...prev[type], file, url: null },
    }));
    if (file) void uploadDoc(type, file);
  };

  const resolveDocUrl = async (type: DocumentType): Promise<string | null> => {
    const doc = docs[type];
    if (doc.url) return doc.url;
    if (doc.file && !doc.uploading) return uploadDoc(type, doc.file);
    if (doc.uploading) return null;
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicleId) return;
    setError(null);

    const reg = docs.registration;
    if (!reg.expiresAt) {
      setError("Registration expiry date is required.");
      return;
    }

    setSubmitting(true);
    try {
      const regUrl = await resolveDocUrl("registration");
      if (!regUrl) {
        setError(
          reg.file || reg.uploading
            ? "Registration document is still uploading. Wait a moment and try again."
            : "Registration document is required. Choose an image file to upload.",
        );
        return;
      }

      const insuranceUrl =
        docs.insurance.file || docs.insurance.url
          ? await resolveDocUrl("insurance")
          : null;
      const roadworthinessUrl =
        docs.roadworthiness.file || docs.roadworthiness.url
          ? await resolveDocUrl("roadworthiness")
          : null;

      await submitVerification(vehicleId, {
        plateNumber: plateNumber.trim(),
        registration: {
          documentUrl: regUrl,
          expiresAt: new Date(reg.expiresAt).toISOString(),
        },
        ...(insuranceUrl && docs.insurance.expiresAt
          ? {
              insurance: {
                documentUrl: insuranceUrl,
                expiresAt: new Date(docs.insurance.expiresAt).toISOString(),
              },
            }
          : {}),
        ...(roadworthinessUrl && docs.roadworthiness.expiresAt
          ? {
              roadworthiness: {
                documentUrl: roadworthinessUrl,
                expiresAt: new Date(
                  docs.roadworthiness.expiresAt,
                ).toISOString(),
              },
            }
          : {}),
      });
      await invalidateFleetQueries(queryClient, {
        orgId,
        vehicleId,
      });
      await refreshVehicles({ force: true });
      navigate(ROUTES.PROVIDER.DASHBOARD, { replace: true });
    } catch (err: any) {
      setError(err?.message || "Verification submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-slate-500">Loading…</p>
      </div>
    );
  }

  const rejectionReason = vehicle?.latestVerification?.rejectionReason;
  const isPending = vehicle?.verificationStatus === "pending";

  return (
    <div className="min-h-screen bg-gray-50 font-display px-4 py-10">
      <div className="max-w-lg mx-auto">
        <Link
          to={ROUTES.PROVIDER.DASHBOARD}
          className="text-sm text-primary font-semibold"
        >
          ← Dashboard
        </Link>
        <h1 className="text-2xl font-black text-slate-900 mt-4 mb-1">
          Vehicle verification
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          Choose each document image (it uploads automatically) and set expiry
          dates. Registration is required.
        </p>

        {vehicles.length > 1 && (
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-500 mb-1">
              Vehicle
            </label>
            <select
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={vehicleId}
              onChange={(e) =>
                navigate(ROUTES.ONBOARDING.VERIFY(e.target.value))
              }
            >
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label || v.plateNumber || v.id} ({v.verificationStatus})
                </option>
              ))}
            </select>
          </div>
        )}

        {isPending && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
            Documents are under review. You can use the dashboard while you
            wait; this vehicle cannot go online until approved.
          </div>
        )}

        {(vehicle?.verificationStatus === "rejected" ||
          vehicle?.verificationStatus === "expired") &&
          rejectionReason && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
              <strong>Previous review:</strong> {rejectionReason}
            </div>
          )}

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5 shadow-sm"
        >
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Plate number
            </label>
            <input
              type="text"
              value={plateNumber}
              onChange={(e) => setPlateNumber(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm"
              required
              disabled={isPending}
            />
          </div>

          {(Object.keys(DOC_LABELS) as DocumentType[]).map((type) => {
            const meta = DOC_LABELS[type];
            const doc = docs[type];
            return (
              <div key={type} className="border-t border-slate-100 pt-4">
                <p className="text-sm font-semibold text-slate-800">
                  {meta.label}
                  {meta.required && <span className="text-red-500"> *</span>}
                </p>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="mt-2 text-sm w-full"
                  disabled={isPending}
                  onChange={(e) =>
                    handleFile(type, e.target.files?.[0] ?? null)
                  }
                />
                {doc.uploading && (
                  <p className="mt-1 text-xs text-slate-500">Uploading…</p>
                )}
                {doc.url && !doc.uploading && (
                  <p className="mt-1 text-xs text-green-600">Uploaded ✓</p>
                )}
                {doc.file && !doc.url && !doc.uploading && (
                  <p className="mt-1 text-xs text-amber-700">
                    Upload did not finish. Choose the file again or submit to
                    retry.
                  </p>
                )}
                <label className="block text-xs text-slate-500 mt-2 mb-1">
                  Expiry date
                </label>
                <input
                  type="date"
                  value={doc.expiresAt}
                  onChange={(e) =>
                    setDocs((prev) => ({
                      ...prev,
                      [type]: { ...prev[type], expiresAt: e.target.value },
                    }))
                  }
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  required={meta.required}
                  disabled={isPending}
                />
              </div>
            );
          })}

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || isPending}
            className="w-full bg-primary text-white font-bold py-3 rounded-xl disabled:opacity-50"
          >
            {isPending
              ? "Under review"
              : submitting
                ? "Submitting…"
                : "Submit for review"}
          </button>
        </form>
      </div>
    </div>
  );
}
